import { useEffect, useId, useRef, useState } from 'react'
import type { CheckboxWidget, Recaptcha, RecaptchaAction } from './recaptcha'

export interface ChallengeBoxProps {
  recaptcha: Recaptcha
  action: RecaptchaAction
  /** Bumped after each submit: the sent token is single-use, so the box is unticked. */
  resetKey: number
  /** The current checkbox token, or null (not ticked, expired, or reset). */
  onToken: (token: string | null) => void
  /** The widget failed to load twice (its one retry used): P-14 with the contact block (UserFlows P-13). */
  onUnavailable: () => void
}

/**
 * P-13 "Please confirm you're not a robot" (UserFlows P-13, Security §3.4, Frontend §4.6): the reCAPTCHA checkbox
 * inline beside Continue (P-03) or Confirm (P-09). Ticking it hands the token up (the button is enabled); an expired
 * tick resets it. If the widget can't load, the patient can retry once; after that, P-14. The copy never gives a
 * reason ("unusual activity" never appears, Security §3.4).
 */
export function ChallengeBox({ recaptcha, action, resetKey, onToken, onUnavailable }: ChallengeBoxProps) {
  const id = useId()
  const container = useRef<HTMLDivElement>(null)
  const widget = useRef<CheckboxWidget | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  const latest = useRef({ onToken, onUnavailable })
  useEffect(() => {
    latest.current = { onToken, onUnavailable }
  })

  // One widget per attempt, in its own child element (a re-render never renders twice into the same element).
  useEffect(() => {
    const parent = container.current
    if (!parent) return
    const host = document.createElement('div')
    parent.appendChild(host)
    let live = true
    setFailed(false)
    recaptcha
      .renderCheckbox(host, action, {
        onToken: (token) => {
          if (live) latest.current.onToken(token)
        },
        onExpired: () => {
          if (live) latest.current.onToken(null)
        },
        onError: () => {
          if (live) latest.current.onToken(null)
        },
      })
      .then(
        (w) => {
          if (live) widget.current = w
        },
        () => {
          if (!live) return
          if (attempt === 0) setFailed(true)
          else latest.current.onUnavailable()
        },
      )
    return () => {
      live = false
      widget.current = null
      host.remove()
    }
  }, [recaptcha, action, attempt])

  // After a submit the token is spent: untick and disable the button again.
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    widget.current?.reset()
    latest.current.onToken(null)
  }, [resetKey])

  return (
    <div className="wg-challenge" role="group" aria-labelledby={`${id}-title`}>
      <p className="wg-challenge-title" id={`${id}-title`}>
        Please confirm you&apos;re not a robot
      </p>
      <div ref={container} className="wg-challenge-widget" />
      {failed && (
        <p className="wg-challenge-retry" role="alert">
          The check didn&apos;t load.{' '}
          <button
            type="button"
            className="link-btn"
            onClick={() => {
              setAttempt((n) => n + 1)
            }}
          >
            Try again
          </button>
        </p>
      )}
    </div>
  )
}
